/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useFhirScopeIfExists, useMessageIndex } from '../chat/hooks/MessageContext';
import { fhirHighlightForceUpdate, selectFhirHighlightForId, setFhirHighlight } from './fhirSlice';

function buildWidgetIdSelectors(baseSelector: string, widgetId: string) {
  const escapedWidgetId = CSS.escape(widgetId);
  return [
    `${baseSelector}[data-fhir-widget-id="${escapedWidgetId}"]`,
    `${baseSelector}[data-fhir-widget-id^="${escapedWidgetId};"]`,
    `${baseSelector}[data-fhir-widget-id*=";${escapedWidgetId};"]`,
    `${baseSelector}[data-fhir-widget-id$=";${escapedWidgetId}"]`,
  ].join(', ');
}

interface FhirHighlightProps {
  children: React.ReactNode;
  fhirWidgetIds?: string[];
  fhirObservationGroupIds?: string[];
  fhirPredefinedWidgetIds?: string[];
  className?: string;
  color?: string;
  documentVersionId?: string;
  pageId?: string;
  inEditorMode?: boolean;
}

/** This wrapper causes the html element with the given id to be highlighted, along with fhir patient MRN and Name.
 * The current highlight is stored in the state (@see selectFhirHighlightForId ).
 *
 *  @see FhirCallout will draw a line between the contextualized word and the widget box
 *
 *  Colors are defined in @see fhirTypes in @see types.ts
 */
export function FhirHighlight({
  children,
  fhirWidgetIds,
  fhirPredefinedWidgetIds,
  documentVersionId,
  pageId,
  color,
  className,
  inEditorMode, // If true, we have CKEditor open and don't hide or collapse the sidebar widgets
}: FhirHighlightProps) {
  const currentlyHighlighted = useAppSelector((state) =>
    selectFhirHighlightForId(state, fhirWidgetIds, fhirPredefinedWidgetIds)
  );
  const messageIndex = useMessageIndex();
  const dispatch = useAppDispatch();
  const fhirScope = useFhirScopeIfExists();
  const scopeSelector = fhirScope
    ? `[data-fhir-scope="${fhirScope.scopeId}"]`
    : `[data-message-index="${messageIndex}"]`;
  const hoveringOverWidget = currentlyHighlighted?.hoveringOverWidget;
  const rightPanelSelector = currentlyHighlighted?.rightPanelWidgetIds
    .map((id) => `div[id="${id}"] .fhir-border`)
    .join(', ');
  const hoveredFhirSelectors =
    hoveringOverWidget && currentlyHighlighted?.predefinedWidgetIds?.length && inEditorMode
      ? currentlyHighlighted.predefinedWidgetIds
          .flatMap((id) => [
            buildWidgetIdSelectors(`${scopeSelector} fhir`, id),
            buildWidgetIdSelectors(`${scopeSelector} div.options_button`, id),
          ])
          .join(', ')
      : '';
  const hoveredOptionButtonSelectors =
    hoveringOverWidget && inEditorMode
      ? fhirPredefinedWidgetIds
          ?.map((id) => buildWidgetIdSelectors(`${scopeSelector} div.options_button`, id))
          .join(', ')
      : '';

  const getFhirTargets = useCallback(() => {
    if (!fhirWidgetIds?.length) return [] as HTMLElement[];
    return fhirWidgetIds
      .map((id) => document.querySelector(`${scopeSelector} fhir[id="${id}"]`) as HTMLElement | null)
      .filter((node): node is HTMLElement => !!node);
  }, [fhirWidgetIds, scopeSelector]);

  /** When we hover over this contextualized panel on the right, set the current highlight to it.
   *  Note that a panel can map to multiple fhirWidgets.
   */
  const setHighlight = useCallback(
    (highlight: boolean) => {
      if (!fhirWidgetIds?.[0]) {
        return;
      }
      dispatch(
        setFhirHighlight(
          highlight
            ? {
                messageIndex,
                fhirWidgetIds,
                predefinedWidgetIds: fhirPredefinedWidgetIds,
                documentVersionId,
                pageId,
                hoveringOverWidget: true,
                inEditorMode: !!inEditorMode,
              }
            : undefined
        )
      );
    },
    [dispatch, documentVersionId, fhirPredefinedWidgetIds, fhirWidgetIds, inEditorMode, messageIndex, pageId]
  );

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (!fhirWidgetIds?.[0]) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('a, button, input, textarea, select')) return;

      setHighlight(true);
      const fhirTargets = getFhirTargets();
      if (fhirTargets.length === 0) return;

      requestAnimationFrame(() => {
        fhirTargets[0]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });

        for (const delay of [0, 75, 150, 300, 500, 800]) {
          setTimeout(() => {
            dispatch(fhirHighlightForceUpdate());
            window.dispatchEvent(new CustomEvent('fhirSidebarScroll'));
          }, delay);
        }
      });
    },
    [dispatch, fhirWidgetIds, getFhirTargets, setHighlight]
  );

  // FhirCallout will draw a line between the 'redbox' and the widget box
  return (
    <div
      onMouseEnter={() => setHighlight(true)}
      onMouseLeave={() => setHighlight(false)}
      onClick={handleClick}
      className={className}
      style={{ display: 'flex' }}
    >
      {fhirWidgetIds?.[0] && currentlyHighlighted && (
        /* Highlight the word (fhirWidgetId) and also highlight
           the widget panel on the right (rightPanelWidgetId).
           We also want to highlight the widget panel with
           the same: data-fhir-predefinedwidget.

           Note FhirCallout does the line */
        <style>
          {`
          ${currentlyHighlighted.fhirWidgetIds.map((id) => `${scopeSelector} fhir[id="${id}"]`).join(', ')}
          ${hoveredFhirSelectors ? `, ${hoveredFhirSelectors}` : ''} {
            /* Underline this span with underline color 'color' and thickness 4 to match FhirCallout */
            text-decoration: underline !important;
            text-decoration-color: ${color} !important;
            text-decoration-thickness: 4px !important;
            text-decoration-skip-ink: none !important;
            text-underline-offset: 2px !important;
          }
          ${rightPanelSelector}
            ${
              inEditorMode
                ? currentlyHighlighted.fhirWidgetIds.map((id) => `, div[data-element-id="${id}"] .fhir-border`)
                : ''
            }
          ${
            inEditorMode
              ? fhirPredefinedWidgetIds?.map((id) => `, fhir[data-fhir-predefined="${id}"] .fhir-border`).join(' ')
              : ''
          }
          {
            border-left-color: ${color} !important;
          }
          ${
            hoveringOverWidget &&
            currentlyHighlighted.fhirWidgetIds
              .map((id) => `${scopeSelector} div.options_button[data-id="${id}"]`)
              .join(', ')
          }
          ${hoveredOptionButtonSelectors ? `, ${hoveredOptionButtonSelectors}` : ''} {
              background-color: ${color} !important;
              transform: scaleX(1) !important;
          }
          }
          #patientContextMRN,
          #patientContextName {
            background: #8199b455;
          }
          .fhircallout {
            color: ${color};
          }
        `}
        </style>
      )}
      {children}
    </div>
  );
}
